/**
 * src/v2/services/organizationService.ts
 * GeoOrgRegistry V2 frontend service — the Organization tier of
 * Country → Organization → Spatial Object → Child Spatial Object.
 *
 * Mirrors coreService.ts for reads. Writes follow the build/sign/submit
 * pattern used by agentService: the backend returns unsigned XDR, the
 * operator's own wallet signs it, and the signed envelope goes back to
 * /organizations/submit.
 *
 * On enumeration: the registry contract is a key/value store with no
 * index, so nothing on chain can list organizations. GeoLink's mirror
 * (`/api/admin/organizations`) is the enumeration source when it is
 * available, and `listOrganizations` then reports `complete: true`.
 *
 * When it is not — an older GeoLink, or a key without the
 * `organizations:write` scope — we fall back to a per-browser directory
 * of ids seen from registrations this browser performed and from
 * spatial object `metadata.organization_id` values, and report
 * `complete: false` so the UI can say the list is partial instead of
 * implying otherwise. Either way every field shown is re-read from the
 * chain; the mirror supplies ids, not truth.
 */

import { V2_API, resolveCountry, numericToAlpha2 } from '../config';
import {
  listMirroredOrganizations,
  tryPushOrganizationToMirror,
} from '../../services/spatial/geoLinkOrganizationMirrorClient';
import { organizationMirrorAvailable } from '../../services/spatial/geoLinkCapabilities';

// ── Types ────────────────────────────────────────────────────────────────────

export type OrgStatusName = 'none' | 'pending' | 'active' | 'suspended' | 'revoked';

export const ORG_STATUS: Record<OrgStatusName, number> = {
  none: 0, pending: 1, active: 2, suspended: 3, revoked: 4,
};

export const ORG_STATUS_NAME: Record<number, OrgStatusName> = {
  0: 'none', 1: 'pending', 2: 'active', 3: 'suspended', 4: 'revoked',
};

/**
 * Which statuses a given status may move to. Mirrors
 * `GeoOrgRegistry::transition_allowed`; duplicated here only to grey out
 * impossible buttons, never as the enforcement point.
 */
export const ORG_STATUS_TRANSITIONS: Record<OrgStatusName, OrgStatusName[]> = {
  none:      [],
  pending:   ['active', 'revoked'],
  active:    ['suspended', 'revoked'],
  suspended: ['active', 'revoked'],
  revoked:   [],
};

export type OrgRoleName = 'admin' | 'operator' | 'beneficiary';

export const ORG_ROLE_BIT: Record<OrgRoleName, number> = {
  admin: 0x01, operator: 0x02, beneficiary: 0x04,
};

export interface OrgVerification {
  verified: boolean;
  verifiedBy: string | null;
  verifiedAt: number | null;
  referenceHash: string | null;
}

/**
 * The three verification dimensions.
 *
 * Not a ladder — three independent evidentiary bars, each gating exactly one
 * capability. `identityVerified` is the same flag the legacy single-boolean
 * toggle writes, so the two views never disagree.
 */
export interface OrgVerificationV2 {
  identityVerified: boolean;
  authorityVerified: boolean;
  beneficiaryVerified: boolean;
  verifiedBy: string | null;
  verifiedAt: number | null;
  referenceHash: string | null;
}

export type OrgVerificationDimension =
  | 'identityVerified'
  | 'authorityVerified'
  | 'beneficiaryVerified';

/** What each dimension unlocks, for labelling the UI. */
export const ORG_DIMENSION_GRANTS: Record<OrgVerificationDimension, string> = {
  identityVerified:    'May be recorded as the owner of spatial objects',
  authorityVerified:   'May act on objects — execute rules, manage operators',
  beneficiaryVerified: 'May be routed value',
};

/**
 * Display mirror of the contract's eligibility reads. Anything that
 * *enforces* a rule must ask the contract; this is for rendering only.
 */
export interface OrgCapabilities {
  canOwnObjects: boolean;
  canOperateObjects: boolean;
  canReceiveFunds: boolean;
}

export interface Organization {
  id: string;                 // 64-char hex
  name: string | null;
  createdBy: string | null;
  createdAt: number | null;
  updatedAt: number | null;
  metadataHash: string | null;
  numericCode: number | null;
  alpha2: string | null;
  status: number;
  statusName: OrgStatusName;
  isActive: boolean;
  verification: OrgVerification | null;
  /** Null when the deployed registry predates the V2 upgrade. */
  verificationV2?: OrgVerificationV2 | null;
  capabilities?: OrgCapabilities;
}

export interface OrgRoles {
  mask: number;
  admin: boolean;
  operator: boolean;
  beneficiary: boolean;
}

export interface OrgRoleReport extends OrgRoles {
  orgId: string;
  wallet: string;
  /** Grants with the organization's status applied — all false when suspended. */
  effective: OrgRoles;
  status: number;
  statusName: OrgStatusName;
}

export interface OrgConfig {
  configured: boolean;
  contractId: string | null;
}

export interface BuiltOrgTx {
  xdr: string;
  latestLedger: number;
}

export interface RegisterOrgResult extends BuiltOrgTx {
  orgId: string;
  numericCode: number;
  alpha2: string | null;
  name: string;
}

export interface OrgSubmitResult {
  hash: string;
  ledger?: number;
}

// ── HTTP plumbing ────────────────────────────────────────────────────────────

const base = (path = ''): string => `${V2_API}/organizations${path}`;

async function asJson<T>(resp: Response): Promise<T> {
  let body: unknown = null;
  try { body = await resp.json(); } catch { /* empty or non-JSON body */ }

  if (!resp.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new Error(err?.error || err?.message || `Request failed: ${resp.status}`);
  }
  return body as T;
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(base(path), {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(body),
  });
  return asJson<T>(resp);
}

const HEX32 = /^[0-9a-f]{64}$/i;

export function isOrgId(value: unknown): value is string {
  return typeof value === 'string' && HEX32.test(value.trim());
}

/** Short display form: first 8 hex characters. */
export function shortOrgId(orgId: string): string {
  return orgId.slice(0, 8);
}

// ── Reads ────────────────────────────────────────────────────────────────────

export async function getOrgConfig(): Promise<OrgConfig> {
  try {
    const resp = await fetch(base('/config'));
    if (!resp.ok) return { configured: false, contractId: null };
    const data = await resp.json();
    return { configured: !!data.configured, contractId: data.contractId ?? null };
  } catch {
    // Backend unreachable is indistinguishable from not deployed for the
    // purpose of deciding whether to render the organizations UI.
    return { configured: false, contractId: null };
  }
}

export async function getOrganization(orgId: string): Promise<Organization | null> {
  if (!isOrgId(orgId)) return null;
  const resp = await fetch(base(`/${orgId.toLowerCase()}`));
  if (resp.status === 404) return null;
  if (!resp.ok) return null;
  return resp.json();
}

export async function getOrgRoles(orgId: string, wallet: string): Promise<OrgRoleReport | null> {
  if (!isOrgId(orgId) || !wallet) return null;
  const resp = await fetch(base(`/${orgId.toLowerCase()}/roles/${wallet}`));
  if (!resp.ok) return null;
  return resp.json();
}

// ── Local organization directory ─────────────────────────────────────────────
//
// See the module header: this exists because nothing can enumerate the
// registry yet. It is a cache of ids, never of organization state — every
// field shown in the UI is re-read from the chain.

const DIRECTORY_KEY = 'geotrust.v2.organizations.known';

function readDirectory(): string[] {
  try {
    const raw = window.localStorage.getItem(DIRECTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(isOrgId).map((s: string) => s.toLowerCase()) : [];
  } catch {
    return [];
  }
}

function writeDirectory(ids: string[]): void {
  try {
    window.localStorage.setItem(DIRECTORY_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch { /* private mode / quota — the directory is a convenience only */ }
}

export function rememberOrganization(orgId: string): void {
  if (!isOrgId(orgId)) return;
  const id = orgId.toLowerCase();
  const known = readDirectory();
  if (!known.includes(id)) writeDirectory([...known, id]);
}

export function forgetOrganization(orgId: string): void {
  if (!isOrgId(orgId)) return;
  const id = orgId.toLowerCase();
  writeDirectory(readDirectory().filter((k) => k !== id));
}

export function knownOrganizationIds(): string[] {
  return readDirectory();
}

export interface OrgListResult {
  organizations: Organization[];
  /**
   * True when the ids came from GeoLink's mirror, which enumerates
   * every organization it knows about. False when we fell back to this
   * browser's directory — surface that, rather than letting the UI
   * imply the list is exhaustive.
   */
  complete: boolean;
  /** Ids we had, but which the chain does not know about. */
  unresolved: string[];
}

/**
 * Where the candidate ids come from. GeoLink's mirror when it is
 * reachable, otherwise this browser's directory.
 *
 * A mirror failure is not fatal: the fallback is exactly the behaviour
 * we had before the mirror existed, and an operator who cannot list
 * organizations is worse off than one looking at a partial list clearly
 * labelled as partial.
 */
async function candidateIds(
  country?: string | number,
): Promise<{ ids: string[]; complete: boolean }> {
  if (await organizationMirrorAvailable()) {
    try {
      const alpha2 = country !== undefined
        ? numericToAlpha2(resolveCountry(country)) ?? undefined
        : undefined;
      const { organizations } = await listMirroredOrganizations({ countryCode: alpha2 });
      const ids = organizations.map((o) => o.id.toLowerCase()).filter(isOrgId);
      // Anything registered from this browser but not yet mirrored (a
      // failed push, or a deployment without the write scope) would
      // otherwise vanish from the operator's own list.
      return { ids: Array.from(new Set([...ids, ...readDirectory()])), complete: true };
    } catch {
      // Fall through to the local directory.
    }
  }
  return { ids: readDirectory(), complete: false };
}

/**
 * Resolve candidate ids against the chain, optionally narrowed to one
 * country. Ids that do not resolve are reported rather than silently
 * dropped, so a stale directory or an over-eager mirror is visible
 * instead of confusing.
 */
export async function listOrganizations(
  opts: { country?: string | number } = {},
): Promise<OrgListResult> {
  const { ids, complete } = await candidateIds(opts.country);
  const settled = await Promise.all(ids.map(async (id) => ({ id, org: await getOrganization(id) })));

  const wanted = opts.country !== undefined ? resolveCountry(opts.country) : null;

  const organizations: Organization[] = [];
  const unresolved: string[] = [];
  for (const { id, org } of settled) {
    if (!org || org.status === ORG_STATUS.none) { unresolved.push(id); continue; }
    if (wanted !== null && org.numericCode !== wanted) continue;
    organizations.push(org);
  }

  organizations.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
  return { organizations, complete, unresolved };
}

// ── Writes (build unsigned XDR) ──────────────────────────────────────────────

export async function buildRegisterOrganization(params: {
  caller: string;
  country: string | number;
  name: string;
  /** Optional 64-hex hash of off-chain metadata (ADR-03). */
  metadataHash?: string | null;
  /** Supply to mirror the id into GeoLink before signing; otherwise minted. */
  orgId?: string;
}): Promise<RegisterOrgResult> {
  const result = await post<RegisterOrgResult & { success: true }>('', {
    caller:       params.caller,
    country:      resolveCountry(params.country),
    name:         params.name,
    metadataHash: params.metadataHash ?? null,
    orgId:        params.orgId,
  });
  return {
    orgId:        result.orgId,
    numericCode:  result.numericCode,
    alpha2:       result.alpha2 ?? numericToAlpha2(result.numericCode),
    name:         result.name,
    xdr:          result.xdr,
    latestLedger: result.latestLedger,
  };
}

export async function buildSetOrgStatus(
  orgId: string,
  caller: string,
  status: OrgStatusName,
): Promise<BuiltOrgTx> {
  return post<BuiltOrgTx>(`/${orgId.toLowerCase()}/status`, { caller, status });
}

export async function buildSetOrgVerification(
  orgId: string,
  caller: string,
  verified: boolean,
  referenceHash?: string | null,
): Promise<BuiltOrgTx> {
  return post<BuiltOrgTx>(`/${orgId.toLowerCase()}/verification`, {
    caller, verified, referenceHash: referenceHash ?? null,
  });
}

/**
 * Sets all three dimensions at once. They must be sent explicitly rather
 * than patched, because the contract writes the whole record — omitting one
 * would silently clear it.
 */
export async function buildSetOrgVerificationV2(
  orgId: string,
  caller: string,
  dimensions: {
    identityVerified: boolean;
    authorityVerified: boolean;
    beneficiaryVerified: boolean;
  },
  referenceHash?: string | null,
): Promise<BuiltOrgTx> {
  return post<BuiltOrgTx>(`/${orgId.toLowerCase()}/verification/v2`, {
    caller, ...dimensions, referenceHash: referenceHash ?? null,
  });
}

export async function buildGrantOrgRole(
  orgId: string,
  caller: string,
  wallet: string,
  roles: OrgRoleName[],
): Promise<BuiltOrgTx> {
  return post<BuiltOrgTx>(`/${orgId.toLowerCase()}/roles`, { caller, wallet, roles });
}

export async function buildRevokeOrgRole(
  orgId: string,
  caller: string,
  wallet: string,
  roles: OrgRoleName[],
): Promise<BuiltOrgTx> {
  return post<BuiltOrgTx>(`/${orgId.toLowerCase()}/roles/revoke`, { caller, wallet, roles });
}

// ── Submit ───────────────────────────────────────────────────────────────────

export async function submitSignedOrgTx(signedXdr: string): Promise<OrgSubmitResult> {
  const data = await post<{ success: true; hash: string; ledger?: number }>('/submit', { signedXdr });
  return { hash: data.hash, ledger: data.ledger };
}

/** Minimal shape of the wallet wrappers used across the app. */
export interface OrgSigner {
  signTransaction(xdr: string, networkPassphrase?: string): Promise<string>;
}

/** build → sign → submit, the sequence every organization write follows. */
export async function signAndSubmitOrgTx(
  built: BuiltOrgTx,
  signer: OrgSigner,
  networkPassphrase?: string,
): Promise<OrgSubmitResult> {
  const signedXdr = await signer.signTransaction(built.xdr, networkPassphrase);
  return submitSignedOrgTx(signedXdr);
}

// ── Mirror ───────────────────────────────────────────────────────────

export interface MirrorOutcome {
  mirrored: boolean;
  /** Why not, when `mirrored` is false. Worth showing the operator. */
  reason?: string;
}

/**
 * Push a confirmed on-chain organization into GeoLink's mirror.
 *
 * Ordering is deliberate and GeoLink is explicit about it: the
 * transaction must have confirmed first, so `ledger` is real. Pushing
 * before submission would leave a phantom row that nothing reconciles
 * away if the transaction is abandoned.
 *
 * Never throws. The chain write has already succeeded by the time this
 * runs, so a mirror failure is a degraded outcome to report, not a
 * reason to tell the operator their registration failed. The binding
 * lands on the next push, because the upsert is idempotent.
 */
export async function mirrorOrganization(
  orgId: string,
  ledger: number | undefined,
): Promise<MirrorOutcome> {
  if (!isOrgId(orgId)) return { mirrored: false, reason: 'Invalid organization id.' };
  if (!ledger) {
    // GeoLink rejects a scoped-key PUT without chain_ledger, and it is
    // right to: the whole point is that automated writes carry the
    // provenance a reconciler needs.
    return { mirrored: false, reason: 'Transaction ledger unknown — cannot mirror without it.' };
  }

  const org = await getOrganization(orgId);
  if (!org) return { mirrored: false, reason: 'Organization not readable on chain yet.' };

  return tryPushOrganizationToMirror({
    id:           org.id,
    name:         org.name ?? '',
    countryCode:  org.alpha2 ?? '',
    status:       org.statusName,
    chainLedger:  ledger,
  });
}
